import { Box, Card, CardContent, Stack, Typography, useMediaQuery } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { colors } from "../../theme/theme";
import ColorSelector from "../Product/ColorSelector";
import { useHorizontalDragScroll } from "../../utils/useHorizontalDragScroll";
import { logUserAction } from "../../utils/analytics";
import ProductAssistantButton from "../AssistantChat/ProductAssistantButton";

const ImageSimilarProductCard = ({
  productData,
  noColor = false,
  oneLiner = false,
  showAll = false,
  verticalLayout = false,
  scrollKey = null,
  actionName = "product_click",
  trackingContext = {},
}) => {
  const navigate = useNavigate();
  const innerScrollRef = useHorizontalDragScroll();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const isSearchPage = window.location.pathname === "/search";
  const query = isSearchPage ? localStorage.getItem("query") : null;

  React.useEffect(() => {
    if (scrollKey && innerScrollRef.current) {
      const savedScroll = sessionStorage.getItem(`scroll_${scrollKey}`);
      if (savedScroll) {
        innerScrollRef.current.scrollLeft = parseInt(savedScroll, 10);
      }
    }
  }, [scrollKey, productData, innerScrollRef]);

  const handleScroll = (e) => {
    if (scrollKey) {
      sessionStorage.setItem(`scroll_${scrollKey}`, e.target.scrollLeft);
    }
  };

  return (
    <Box
      ref={innerScrollRef}
      onScroll={handleScroll}
      className="flex overflow-x-auto"
      sx={{
        display: verticalLayout ? "grid" : "flex",
        gridTemplateColumns: verticalLayout ? "repeat(3, minmax(270px, 1fr))" : "none",
        flexDirection: verticalLayout ? "none" : "row",
        gap: 1.5,
        pb: 1.5,
        marginLeft: "-20px",
        pr: 1.5,
        pl: oneLiner ? 1 : 1.5,
        "&::-webkit-scrollbar": {
          display: "none",
        },
        ml: 0,
        pt: 0.5,
      }}
    >
      {productData.map(
        (product, index) =>
          (showAll || index !== 0) && (() => {
            const handleCardClick = () => {
              logUserAction({
                action: actionName,
                data: {
                  product_id: product.product_id || null,
                  product_title: product.title || null,
                  brand: product.brand || null,
                  product_price:
                    product?.price?.discounted ||
                    product?.price?.original ||
                    null,
                  currency: product?.price?.currency || null,
                  product_image: product?.image_url || null,
                  ...trackingContext
                },
                timestamp: new Date().toISOString(),
              });
              navigate("/product", {
                state: {
                  product: product,
                  similarProducts: productData,
                  searchQuery: query,
                  trackingContext,
                },
              });
            };

            return (
              <Stack
                key={index}
                direction={"column"}
                sx={{
                  ml: index === 0 ? 2 : 0,
                }}
              >
                {isMobile ? (
                  <Box
                    onClick={handleCardClick}
                    sx={{
                      width: "160px",
                      height: "240px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      position: "relative",
                      cursor: "pointer",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      backgroundColor: "#F9F9F9",
                      willChange: "auto",
                      "&:active": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <img
                      src={product?.image_url}
                      alt={product.title || "product image"}
                      loading="lazy"
                      onError={(e) => {
                        const fallbackImage = product?.images?.[0];
                        if (fallbackImage && !e.target.dataset.triedFallback) {
                          e.target.dataset.triedFallback = "true";
                          e.target.src = fallbackImage;
                        } else {
                          e.target.onerror = null;
                          e.target.src = require("../../assets/images/no_image.png");
                        }
                      }}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <ProductAssistantButton product={product} />
                    {product.price?.discounted && product.price?.original && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          background: "#FF3B30",
                          color: "#FFF",
                          px: 1,
                          py: 0.5,
                          borderRadius: "4px",
                          fontFamily: "Poppins-Bold",
                          fontSize: "9px",
                          letterSpacing: "0.5px"
                        }}
                      >
                        SALE
                      </Box>
                    )}

                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
                        px: 1.5,
                        pt: 4,
                        pb: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25
                      }}
                    >
                      {product.brand && (
                        <Typography
                          sx={{
                            fontFamily: "Poppins-SemiBold",
                            fontSize: "11px",
                            color: "#FFF",
                            letterSpacing: "0.2px",
                            textTransform: "uppercase",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {product.brand}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          fontFamily: "Poppins-Regular",
                          fontSize: "11px",
                          color: "rgba(255,255,255,0.9)",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {product.title}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
                        {product.price?.discounted ? (
                          <>
                            <Typography
                              sx={{
                                fontFamily: "Poppins-SemiBold",
                                fontSize: "12px",
                                color: "#FF5252",
                              }}
                            >
                              {product.price.discounted} {product.price.currency}
                            </Typography>
                            {product.price.original && (
                              <Typography
                                sx={{
                                  fontFamily: "Poppins-Regular",
                                  fontSize: "10px",
                                  color: "rgba(255,255,255,0.6)",
                                  textDecoration: "line-through",
                                }}
                              >
                                {product.price.original} {product.price.currency}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: "Poppins-SemiBold",
                              fontSize: "12px",
                              color: "#FFF",
                            }}
                          >
                            {product.price?.original || "N/A"} {product.price?.currency || ""}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Card
                    onClick={handleCardClick}
                    sx={{
                      width: verticalLayout ? "100%" : (noColor ? "200px" : "220px"),
                      borderRadius: noColor ? "12px" : "16px",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                      border: "none",
                      flexShrink: 0,
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                      "&:hover": {
                        boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                        transform: "translateY(-6px)",
                        "& .product-img": {
                          transform: "scale(1.05)",
                        },
                      },
                      "&:active": {
                        transform: "translateY(-2px)",
                      },
                      cursor: "pointer",
                    }}
              >
                <Box
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  <img
                    src={product?.image_url}
                    onError={(e) => {
                      const fallbackImage = product?.images?.[0];
                      if (fallbackImage && !e.target.dataset.triedFallback) {
                        e.target.dataset.triedFallback = "true";
                        e.target.src = fallbackImage;
                      } else {
                        e.target.onerror = null;
                        e.target.src = require("../../assets/images/no_image.png");
                      }
                    }}
                    loading="lazy"
                    alt=""
                    className="product-img"
                    style={{
                      objectFit: "cover",
                      objectPosition: "top",
                      height: noColor ? "380px" : "400px",
                      width: "100%",
                      transition: "transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1)",
                    }}
                  />
                  <ProductAssistantButton product={product} />
                </Box>

                <CardContent
                  className="bg-white"
                  sx={{
                    p: noColor ? 2 : 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    flexGrow: 1,
                  }}
                >
                  {product?.brand && (
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#888",
                        fontFamily: "Poppins-Medium",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        mb: -0.5,
                      }}
                    >
                      {product.brand}
                    </Typography>
                  )}
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: noColor ? "14px" : "14px",
                      color: "#1a1a1a",
                      fontFamily: "Poppins-Medium",
                      fontWeight: "500",
                      lineHeight: 1.4,
                      display: "-webkit-box",
                      WebkitLineClamp: oneLiner ? 2 : 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      minHeight: oneLiner ? "auto" : "40px",
                    }}
                  >
                    {product?.title}
                  </Typography>

                  {!noColor && <ColorSelector small />}

                  <Box sx={{ mt: "auto", pt: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: noColor ? "16px" : "15px",
                        color: product?.price?.discounted ? "#E53935" : "#1a1a1a",
                        fontFamily: "Poppins-SemiBold",
                        fontWeight: "700",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {product?.price?.discounted ? (
                        <>
                          {product.price.discounted}{" "}
                          <Typography
                            component="span"
                            sx={{
                              fontSize: noColor ? "13px" : "12px",
                              color: "#666",
                              fontFamily: "Poppins-Regular",
                              fontWeight: "400",
                            }}
                          >
                            {product.price.currency || ""}
                          </Typography>
                          {product.price.original && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: noColor ? "13px" : "12px",
                                color: "#999",
                                textDecoration: "line-through",
                                ml: 1,
                                fontFamily: "Poppins-Regular",
                                fontWeight: "400",
                              }}
                            >
                              {product.price.original}{" "}
                              {product.price.currency || ""}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          {product?.price?.original || "N/A"}{" "}
                          <Typography
                            component="span"
                            sx={{
                              fontSize: noColor ? "13px" : "12px",
                              color: "#666",
                              fontFamily: "Poppins-Regular",
                              fontWeight: "400",
                            }}
                          >
                            {product?.price?.currency || ""}
                          </Typography>
                        </>
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            </Stack>
          );
        })()
      )}
    </Box>
  );
};

export default ImageSimilarProductCard;
